import { useEffect, useState, useCallback } from "react";
import { StyleSheet, View, ActivityIndicator, Pressable, RefreshControl, ScrollView } from "react-native";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { twilioSmsService, SMSConversation } from "@/services/twilioSmsService";
import { useSMSPhone } from "./_layout";
import { formatDistanceToNow } from "date-fns";
import { formatPhoneNumber } from "@/lib/phone";
import { userService } from "@/services/userService";
import Avatar from "@/components/Avatar";

type MatchedUser = {
  id: string;
  name: string;
};

// Normalize phone to 10-digit format (strip +1 or leading 1)
function normalizeToTenDigits(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  return digits;
}

export default function SMSConversationListPage() {
  const { phoneNumber } = useSMSPhone();
  const { colors } = useTheme();
  const [conversations, setConversations] = useState<SMSConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usersByPhone, setUsersByPhone] = useState<Map<string, MatchedUser>>(new Map());

  // Load users for phone lookup
  useEffect(() => {
    if (conversations.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const users = await userService.getUsersByCellPhones(conversations.map((c) => c.externalNumber));
        if (cancelled) return;
        const phoneMap = new Map<string, MatchedUser>();
        for (const u of users) {
          phoneMap.set(u.last10, {
            id: u.id,
            name: u.preferred_name || `${u.first_name || ""} ${u.last_name || ""}`.trim(),
          });
        }
        setUsersByPhone(phoneMap);
      } catch (err) {
        console.error("Failed to load users for phone lookup:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversations]);

  // Helper to get matched user for a phone number
  const getMatchedUser = useCallback(
    (phone: string): MatchedUser | null => {
      const normalized = normalizeToTenDigits(phone);
      return usersByPhone.get(normalized) || null;
    },
    [usersByPhone],
  );

  const fetchConversations = useCallback(async () => {
    if (!phoneNumber) return;

    try {
      setError(null);
      const data = await twilioSmsService.getConversations(phoneNumber);
      setConversations(data);
    } catch (err: any) {
      console.error("Failed to fetch SMS conversations:", err);
      setError(err.message || "Failed to load conversations");
    }
  }, [phoneNumber]);

  useEffect(() => {
    setLoading(true);
    fetchConversations().finally(() => setLoading(false));
  }, [fetchConversations]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchConversations();
    setRefreshing(false);
  }, [fetchConversations]);

  const handleConversationPress = (externalNumber: string) => {
    // Navigate to the conversation detail
    const encodedPhone = encodeURIComponent(phoneNumber);
    const encodedExternal = encodeURIComponent(externalNumber);
    router.push(`/chat/sms/${encodedPhone}/${encodedExternal}`);
  };

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <ThemedText style={styles.loadingText}>Loading conversations...</ThemedText>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centerContainer}>
        <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>{error}</ThemedText>
      </View>
    );
  }

  if (conversations.length === 0) {
    return (
      <View style={styles.centerContainer}>
        <ThemedText style={styles.emptyText}>No SMS conversations yet</ThemedText>
        <ThemedText style={[styles.emptySubtext, { color: colors.placeholder }]}>
          Send a message to start a conversation
        </ThemedText>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.primary} />}
    >
      {conversations.map((conv) => {
        const hasUnread = conv.unread;
        const matchedUser = getMatchedUser(conv.externalNumber);

        return (
          <Pressable
            key={conv.externalNumber}
            style={({ hovered }) => [
              styles.conversationItem,
              { borderColor: colors.border },
              hovered && { backgroundColor: colors.card },
            ]}
            onPress={() => handleConversationPress(conv.externalNumber)}
          >
            <View style={styles.conversationContent}>
              <View style={styles.conversationHeader}>
                {matchedUser ? (
                  <View style={styles.userWithAvatar}>
                    {hasUnread && <View style={[styles.unreadDot, { backgroundColor: colors.primary }]} />}
                    <Avatar userId={matchedUser.id} size={36} />
                    <View style={styles.userInfo}>
                      <ThemedText style={[styles.userName, hasUnread && styles.unreadText]}>
                        {matchedUser.name}
                      </ThemedText>
                      <ThemedText style={[styles.userPhone, { color: colors.placeholder }]}>
                        {formatPhoneNumber(conv.externalNumber)}
                      </ThemedText>
                    </View>
                  </View>
                ) : (
                  <View style={styles.phoneWithIndicator}>
                    {hasUnread && <View style={[styles.unreadDot, { backgroundColor: colors.primary }]} />}
                    <ThemedText style={[styles.phoneNumber, hasUnread && styles.unreadText]}>
                      {formatPhoneNumber(conv.externalNumber)}
                    </ThemedText>
                  </View>
                )}
                <ThemedText style={[styles.timestamp, { color: colors.placeholder }]}>
                  {formatDistanceToNow(new Date(conv.lastMessageDate), { addSuffix: true })}
                </ThemedText>
              </View>
              <View style={[styles.messagePreview, matchedUser && styles.messagePreviewWithAvatar]}>
                {conv.direction === "inbound" && (
                  <ThemedText style={[styles.directionIndicator, { color: colors.placeholder }]}>Received: </ThemedText>
                )}
                <ThemedText
                  style={[styles.lastMessage, { color: colors.placeholder }, hasUnread && styles.unreadPreview]}
                  numberOfLines={1}
                >
                  {conv.lastMessage}
                </ThemedText>
              </View>
            </View>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  centerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  loadingText: {
    marginTop: 12,
    opacity: 0.7,
  },
  errorText: {
    textAlign: "center",
  },
  emptyText: {
    fontSize: 18,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
  },
  conversationItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  conversationContent: {
    gap: 4,
  },
  conversationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  phoneWithIndicator: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: 8,
  },
  userWithAvatar: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: 10,
  },
  userInfo: {
    flex: 1,
    gap: 2,
  },
  userName: {
    fontSize: 16,
    fontWeight: "600",
  },
  userPhone: {
    fontSize: 12,
  },
  unreadDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  phoneNumber: {
    fontSize: 16,
    fontWeight: "600",
  },
  unreadText: {
    fontWeight: "700",
  },
  timestamp: {
    fontSize: 12,
  },
  messagePreview: {
    flexDirection: "row",
    paddingLeft: 18, // Align with phone number after dot space
  },
  messagePreviewWithAvatar: {
    paddingLeft: 56, // Align with name (after dot + avatar + gap)
  },
  directionIndicator: {
    fontSize: 14,
    fontStyle: "italic",
  },
  lastMessage: {
    fontSize: 14,
    flex: 1,
  },
  unreadPreview: {
    fontWeight: "500",
  },
});
